import { Link, useLocation } from "react-router-dom";
import { Home, Trophy, Users, User, Mail } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { cn } from "@/lib/utils";

interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
  requiresAuth?: boolean;
  badge?: number;
}

export const MobileBottomNav = () => {
  const location = useLocation();
  const { user } = useAuth();
  const unreadCount = useUnreadMessages();

  const navItems: NavItem[] = [
    { label: "Home", path: "/", icon: Home },
    { label: "Challenges", path: "/challenges", icon: Trophy },
    { label: "Community", path: "/community", icon: Users },
    { label: "Inbox", path: "/inbox", icon: Mail, requiresAuth: true, badge: unreadCount },
    { label: user ? "Profile" : "Sign In", path: user ? "/profile" : "/auth", icon: User },
  ];

  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  // Hide on auth page, admin dashboard, and for non-authenticated users
  const hiddenPaths = ["/auth", "/admin"];
  if (hiddenPaths.some(p => location.pathname.startsWith(p)) || !user) {
    return null;
  }

  return (
    <>
      {/* Spacer to prevent content from being hidden behind nav */}
      <div className="h-16 md:hidden" />
      
      {/* Bottom Navigation */}
      <nav 
        className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-background/95 backdrop-blur-md border-t border-border safe-area-inset-bottom"
        role="navigation"
        aria-label="Mobile navigation"
      >
        <div className="flex items-center justify-around h-16 px-2">
          {navItems.map((item) => {
            // Skip auth-required items for non-authenticated users
            if (item.requiresAuth && !user) return null;
            
            const active = isActive(item.path);
            const Icon = item.icon;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "flex flex-col items-center justify-center flex-1 h-full px-1 py-2 transition-colors touch-manipulation relative",
                  active 
                    ? "text-primary" 
                    : "text-muted-foreground hover:text-foreground"
                )}
                aria-current={active ? "page" : undefined}
              >
                <div className="relative">
                  <Icon className="w-5 h-5 mb-1" strokeWidth={active ? 2.5 : 2} />
                  {/* Badge for unread messages */}
                  {item.badge && item.badge > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-bold text-primary-foreground bg-primary rounded-full">
                      {item.badge > 99 ? "99+" : item.badge}
                    </span>
                  )}
                </div>
                <span className={cn(
                  "text-[10px] font-medium truncate max-w-full",
                  active && "font-semibold"
                )}>
                  {item.label}
                </span>
                {/* Active indicator line */}
                {active && (
                  <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary rounded-full" />
                )}
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
};